import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, X, Eye } from 'lucide-react';
import { emailOtpAdminApi, EmailOtpRecord, eventsApi } from '../../services/api';
import type { Event } from '../../types';

const STATUS_COLORS: Record<string, string> = {
  created: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-blue-50 text-blue-700 border-blue-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
  verified: 'bg-green-50 text-green-700 border-green-200',
  expired: 'bg-orange-50 text-orange-700 border-orange-200',
  invalidated: 'bg-red-100 text-red-800 border-red-300',
};

const EmailVerification: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<EmailOtpRecord[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [filterEventId, setFilterEventId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterIp, setFilterIp] = useState('');

  const [selected, setSelected] = useState<EmailOtpRecord | null>(null);

  const fetchEvents = async () => {
    try {
      const resp = await eventsApi.getAll({ limit: 500 });
      setEvents(resp.data.data || []);
    } catch {
      setEvents([]);
    }
  };

  const fetchRecords = async (pg = page) => {
    setLoading(true);
    try {
      const resp = await emailOtpAdminApi.list({
        page: pg,
        limit,
        eventId: filterEventId || undefined,
        status: filterStatus || undefined,
        email: filterEmail || undefined,
        ip: filterIp || undefined,
      });
      setRecords(resp.data.data || []);
      setTotal((resp.data as any).pagination?.total ?? (resp.data.data?.length ?? 0));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load email verification records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchRecords(); }, [page, filterEventId, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => { setPage(1); fetchRecords(1); };

  const handleClear = () => {
    setFilterEmail('');
    setFilterIp('');
    setFilterStatus('');
    setFilterEventId('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const statusBadge = (status: string) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
      {status.toUpperCase()}
    </span>
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Email verification monitoring</h1>
        <button onClick={() => { setPage(1); fetchRecords(1); }} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap gap-3">
          <select value={filterEventId} onChange={e => { setPage(1); setFilterEventId(e.target.value); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64">
            <option value="">Filter by Knowledge Assistant</option>
            {events.map(ev => <option key={ev._id} value={ev._id}>{ev.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => { setPage(1); setFilterStatus(e.target.value); }} className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-44">
            <option value="">All statuses</option>
            {['created', 'sent', 'failed', 'verified', 'expired', 'invalidated'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={filterEmail} onChange={e => setFilterEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Email" className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" />
          <input value={filterIp} onChange={e => setFilterIp(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="IP" className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-44" />
          <button onClick={handleSearch} className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Search</button>
          <button onClick={handleClear} className="px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50">Clear</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Status', 'Email', 'Knowledge Assistant', 'Attempts', 'Resends', 'Provider', 'Last error', 'Created', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : records.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No records found</td></tr>
              ) : records.map(r => (
                <tr key={r._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{statusBadge(r.status)}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-xs truncate">{r.emailLower}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">
                    {events.find(ev => ev._id === r.eventId)?.name || <code className="text-xs text-gray-500">{r.eventId}</code>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.attemptCount}</td>
                  <td className="px-4 py-3 text-gray-700">{r.resendCount}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">{r.provider}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-[180px] truncate">{r.lastErrorMessage || '–'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelected(r)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        title="View details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      <button
                        disabled={r.status === 'verified' || r.status === 'invalidated'}
                        onClick={async () => {
                          try {
                            await emailOtpAdminApi.resend(r._id);
                            toast.success('Resend triggered');
                            fetchRecords();
                          } catch (e: any) {
                            toast.error(e?.response?.data?.message || 'Resend failed');
                          }
                        }}
                        className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >Resend</button>
                      <button
                        disabled={r.status === 'verified' || r.status === 'invalidated'}
                        onClick={async () => {
                          try {
                            await emailOtpAdminApi.invalidate(r._id);
                            toast.success('Invalidated');
                            fetchRecords();
                          } catch (e: any) {
                            toast.error(e?.response?.data?.message || 'Invalidate failed');
                          }
                        }}
                        className="px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >Invalidate</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">Total: {total}</p>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-600">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Verification record</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 grid grid-cols-[180px_1fr] gap-x-4 gap-y-3 text-sm">
              <span className="font-medium text-gray-600">ID</span><code className="text-xs text-gray-700 break-all">{selected._id}</code>
              <span className="font-medium text-gray-600">Status</span><div>{statusBadge(selected.status)}</div>
              <span className="font-medium text-gray-600">Email</span><span className="text-gray-800">{selected.emailLower}</span>
              <span className="font-medium text-gray-600">Knowledge Assistant</span><span className="text-gray-800">{events.find(e => e._id === selected.eventId)?.name || selected.eventId}</span>
              <span className="font-medium text-gray-600">Expires</span><span className="text-gray-800">{new Date(selected.expiresAt).toLocaleString()}</span>
              <span className="font-medium text-gray-600">Attempts</span><span className="text-gray-800">{selected.attemptCount}</span>
              <span className="font-medium text-gray-600">Resends</span><span className="text-gray-800">{selected.resendCount}</span>
              <span className="font-medium text-gray-600">Provider</span><span className="text-gray-800">{selected.provider}</span>
              <span className="font-medium text-gray-600">Provider status</span><span className="text-gray-800">{selected.providerStatus || '–'}</span>
              <span className="font-medium text-gray-600">Provider message ID</span><span className="text-gray-800 break-all">{selected.providerMessageId || '–'}</span>
              <span className="font-medium text-gray-600">Last error</span><span className="text-gray-800">{selected.lastErrorMessage || '–'}</span>
              <span className="font-medium text-gray-600">Request IP</span><span className="text-gray-800">{selected.requestIp || '–'}</span>
              <span className="font-medium text-gray-600">User agent</span><span className="text-gray-700 break-words">{selected.userAgent || '–'}</span>
              <span className="font-medium text-gray-600">Created</span><span className="text-gray-800">{new Date(selected.createdAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailVerification;
